import React from 'react';
import { styled } from '@mui/material/styles';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import { CommonCode } from '../../models/admin/CommonCode';

const SELECT_ALL: CommonCode = {
  cmnCd: 'ALL',
  cmnCdNm: 'All cop_cd',
};

type Props = {
  options: CommonCode[];
  value: CommonCode[];
  onChange: (value: CommonCode[]) => void;
  showSelectAll?: boolean;
  disabled?: boolean | (() => boolean);
  placeholder?: string;
};

/* 스타일 */
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  minWidth: '0 !important',
  backgroundColor: 'white',

  /* ✅ Autocomplete 핵심: 한 줄 고정 */
  '& .MuiAutocomplete-inputRoot': {
    height: 32,
    paddingRight: '0 !important',
    flexWrap: 'nowrap', // 줄바꿈 금지
    alignItems: 'center',
  },

  /* DatePicker 톤: root */
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    color: '#666 !important',
    fontWeight: 'inherit',
    height: 32,
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#C9CDD4 !important', // BorderColor.Form 로 교체 가능
    borderRadius: '2px',
    borderWidth: '1px !important',
    top: '0 !important',
    right: '0 !important',
    bottom: '0 !important',
    left: '0 !important',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px !important',
  },

  '& .MuiAutocomplete-input': {
    minWidth: 0,
    flexGrow: 1,
    fontSize: 13,
    margin: '0 !important',
  },

  /* 아이콘 */
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: '#444',
  },

  /* disabled 스타일 추가 */
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: '#f5f5f5',
    color: '#999 !important',
    cursor: 'not-allowed',
  },

  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9 !important',
  },

  '& .MuiOutlinedInput-root.Mui-disabled .MuiAutocomplete-input': {
    WebkitTextFillColor: '#999',
    color: '#999 !important',
  },

  '& .MuiOutlinedInput-root.Mui-disabled .MuiSvgIcon-root': {
    color: '#bdbdbd',
  },
}));

/* 컴포넌트 */
export default function GMultiSelect({
  options,
  value,
  onChange,
  showSelectAll = false,
  disabled = false,
  placeholder = '선택하세요',
}: Props) {
  return (
    <StyledAutocomplete
      multiple
      disabled={typeof disabled === 'function' ? disabled() : disabled ?? false}
      options={showSelectAll ? [SELECT_ALL, ...options] : options}
      value={value}
      onChange={(_, nv) => {
        const list = nv as CommonCode[];

        if (showSelectAll && list.some((v) => v.cmnCd === 'ALL')) {
          onChange(options);
          return;
        }

        onChange(list.filter((v) => v.cmnCd !== 'ALL'));
      }}
      getOptionLabel={(o) => (o as CommonCode).cmnCd || ''}
      isOptionEqualToValue={(o, v) => (o as CommonCode).cmnCd === (v as CommonCode).cmnCd}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => {
        const item = option as CommonCode;

        return (
          <li {...props}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                height: '28px',
              }}
            >
              <Checkbox
                checked={selected}
                sx={{
                  '&.Mui-checked': {
                    color: 'rgb(45, 155, 178)',
                  },
                }}
              />
              {item.cmnCd}
            </div>
          </li>
        );
      }}
      renderTags={(tagValue) => {
        const list = tagValue as CommonCode[];
        const MAX = 2;
        const visibleList = list.slice(0, MAX);
        const more = list.length - MAX;

        return (
          <div>
            {visibleList.map((item) => (
              <Chip
                key={item.cmnCd}
                label={item.cmnCd}
                sx={{
                  height: '90%', // 원하는 Chip 높이
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  },
                }}
                onDelete={() => {
                  onChange(value.filter((v) => v.cmnCd !== item.cmnCd));
                }}
              />
            ))}

            {more > 0 && <span> +{more}</span>}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={value.length === 0 ? placeholder : ''} />
      )}
    />
  );
}
